import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Save, Sparkles, Info, X } from 'lucide-react'
import AppShell from '../../components/b2b/AppShell'
import BlockEditor from '../../components/b2b/BlockEditor'
import ConsentCheckboxes from '../../components/ConsentCheckboxes'
import { api, APIError } from '../../api/client'
import { LEGAL_VERSIONS } from '../../config/legalVersions'

const DEFAULT_BLOCKS = [
  { id: 'preset-about', label: 'About the job', content: '' },
  { id: 'preset-responsibilities', label: 'Responsibilities', content: '' },
  { id: 'preset-tech-stack', label: 'Tech stack', content: '' },
  { id: 'preset-required-experience', label: 'Required experience', content: '' },
]

// Compass § 4 taxonomy — keep in lockstep with
// FJCS-backend/app/services/b2b_ai_prompts_roles.py::VALID_ROLE_TYPES.
// Each role drives different AI question patterns at screening time
// (system design probes for tech, role-play for sales, CIDS for execs, etc).
const ROLE_TYPE_OPTIONS = [
  { value: 'general',            label: 'General / Other' },
  { value: 'tech_engineering',   label: 'Tech / Engineering (SWE, SRE, ML, DevOps)' },
  { value: 'data_analytics',     label: 'Data / Analytics / Data Science' },
  { value: 'product_management', label: 'Product Management' },
  { value: 'design',             label: 'Design (UX / Product / Visual)' },
  { value: 'sales',              label: 'Sales (SDR, AE, AM, SE)' },
  { value: 'customer_support',   label: 'Customer Support / Customer Success' },
  { value: 'executive',          label: 'Executive / Leadership' },
  { value: 'creative_marketing', label: 'Creative / Marketing / Brand' },
]

function roleTypeLabel(value) {
  return ROLE_TYPE_OPTIONS.find((o) => o.value === value)?.label || 'General / Other'
}

/**
 * Company selector for the job form: a dropdown of the workspace's companies
 * plus an inline "＋ New company" that creates one and selects it. Optional —
 * "— No company —" leaves the job unlinked.
 */
function CompanyPicker({ value, onChange, disabled }) {
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [newName, setNewName] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    api.get('/api/b2b/companies')
      .then((d) => { if (!cancelled) setCompanies(d) })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const handleCreate = async () => {
    setError('')
    if (!newName.trim()) { setError('Enter a company name.'); return }
    setCreating(true)
    try {
      const created = await api.post('/api/b2b/companies', { name: newName.trim() })
      setCompanies((prev) =>
        [...prev, { id: created.id, name: created.name, job_count: 0 }]
          .sort((a, b) => a.name.localeCompare(b.name)),
      )
      onChange(created.id)
      setAdding(false)
      setNewName('')
    } catch (err) {
      setError(err instanceof APIError ? (err.data?.detail || 'Could not create company.') : 'Could not create company.')
    } finally {
      setCreating(false)
    }
  }

  return (
    <>
      <Field label="Company" hint="The organization this job is opened for. Optional.">
        {adding ? (
          <div className="flex-1 flex items-center gap-2">
            <input
              type="text"
              autoFocus
              placeholder="New company name"
              className="flex-1 text-[15px] outline-none bg-transparent"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              disabled={creating}
            />
            <button type="button" onClick={handleCreate} disabled={creating}
              className="text-sm font-bold text-accent hover:underline disabled:opacity-50">
              {creating ? 'Adding…' : 'Add'}
            </button>
            <button type="button" onClick={() => { setAdding(false); setNewName(''); setError('') }} disabled={creating}
              className="text-sm text-ink-3 hover:text-ink-1">
              Cancel
            </button>
          </div>
        ) : (
          <select
            className="flex-1 text-[15px] outline-none bg-transparent"
            value={value || ''}
            onChange={(e) => {
              if (e.target.value === '__new__') { setAdding(true); return }
              onChange(e.target.value)
            }}
            disabled={disabled || loading}
          >
            <option value="">— No company —</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            <option value="__new__">＋ New company…</option>
          </select>
        )}
      </Field>
      {error && <p className="text-[11px] text-red-600 ml-1 -mt-2">{error}</p>}
    </>
  )
}

export default function JobConstructorScreen() {
  const navigate = useNavigate()
  const { jobId } = useParams()
  const isEdit = Boolean(jobId)
  const [searchParams] = useSearchParams()

  const [title, setTitle] = useState('')
  // Prefilled from ?company=<id> when arriving from a company page (new jobs only).
  const [companyId, setCompanyId] = useState(isEdit ? '' : (searchParams.get('company') || ''))
  const [department, setDepartment] = useState('')
  const [location, setLocation] = useState('')
  const [statusValue, setStatusValue] = useState('draft')
  const [language, setLanguage] = useState('en')
  // Compass § 4 role taxonomy. Default 'general' until the AI classifier
  // (Celery task) refines it from the job description. Recruiter can also
  // override manually — we track auto_detected so we know whether to show
  // the auto-suggest banner and whether the next save should be sticky.
  const [roleType, setRoleType] = useState('general')
  const [roleTypeAutoDetected, setRoleTypeAutoDetected] = useState(true)
  const [roleTypeConfidence, setRoleTypeConfidence] = useState(null)
  // EU Pay Transparency Directive 2026 disclosure. All four fields move
  // together — empty UI means "not yet disclosed" (backend stores NULL).
  const [salaryCurrency, setSalaryCurrency] = useState('EUR')
  const [salaryMin, setSalaryMin] = useState('')          // whole units (e.g. €60000)
  const [salaryMax, setSalaryMax] = useState('')
  const [salaryPeriod, setSalaryPeriod] = useState('year')
  const [blocks, setBlocks] = useState(isEdit ? [] : DEFAULT_BLOCKS)
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [eeoBannerDismissed, setEeoBannerDismissed] = useState(false)
  const [consent, setConsent] = useState({ compliance_attestation: false })
  const updateConsent = (key, value) => setConsent((prev) => ({ ...prev, [key]: value }))
  const isPublishing = statusValue === 'active'

  useEffect(() => {
    if (!isEdit) return
    let cancelled = false
    api.get(`/api/b2b/jobs/${jobId}`)
      .then((job) => {
        if (cancelled) return
        setTitle(job.title || '')
        setCompanyId(job.company_id || '')
        setDepartment(job.department || '')
        setLocation(job.location || '')
        setStatusValue(job.status || 'draft')
        setLanguage(job.language || 'en')
        setRoleType(job.role_type || 'general')
        setRoleTypeAutoDetected(job.role_type_auto_detected !== false)
        setRoleTypeConfidence(
          job.role_type_confidence != null ? Number(job.role_type_confidence) : null
        )
        const sr = job.salary_range
        if (sr) {
          setSalaryCurrency(sr.currency || 'EUR')
          // Backend stores in minor units (cents/pence). UI works in whole
          // currency for readability.
          setSalaryMin(sr.min_minor != null ? String(Math.round(sr.min_minor / 100)) : '')
          setSalaryMax(sr.max_minor != null ? String(Math.round(sr.max_minor / 100)) : '')
          setSalaryPeriod(sr.period || 'year')
        }
        setBlocks(
          (job.blocks || []).map((b) => ({ id: b.id, label: b.label, content: b.content }))
        )
      })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof APIError && err.status === 404) {
          setError('Job not found.')
        } else {
          setError('Could not load job.')
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [isEdit, jobId])

  const handleSave = async () => {
    setError('')

    if (!title.trim()) {
      setError('Title is required.')
      return
    }

    if (isPublishing && !consent.compliance_attestation) {
      setError('Please confirm this posting complies with applicable employment laws before publishing.')
      return
    }

    const payload = {
      title: title.trim(),
      company_id: companyId || null,
      department: department.trim() || null,
      location: location.trim() || null,
      status: statusValue,
      language,
      blocks: blocks.map((b, idx) => ({
        // Передаём id только если это серверный UUID (не preset).
        id: isServerUuid(b.id) ? b.id : undefined,
        label: b.label.trim() || `Section ${idx + 1}`,
        content: b.content || '',
        position: idx,
      })),
    }
    // Send role_type only when the recruiter has manually picked a value
    // — sending it on auto-detected jobs would prematurely flip
    // role_type_auto_detected=false on the backend. New jobs without a
    // manual override leave the field undefined → backend triggers the
    // classifier Celery task.
    if (!roleTypeAutoDetected) {
      payload.role_type = roleType
    }

    if (isPublishing) {
      payload.compliance_attestation = {
        version: LEGAL_VERSIONS.COMPLIANCE_ATTESTATION,
        confirmed: true,
      }
    }

    // Salary range is sent only when the recruiter has filled in BOTH
    // bounds (all four fields move together — backend rejects partials).
    // Empty strings clear the disclosure (sends salary_range: null).
    const minNum = Number(salaryMin)
    const maxNum = Number(salaryMax)
    const minSet = salaryMin !== '' && Number.isFinite(minNum) && minNum >= 0
    const maxSet = salaryMax !== '' && Number.isFinite(maxNum) && maxNum >= 0
    if (minSet && maxSet) {
      if (maxNum < minNum) {
        setError('Salary maximum must be greater than or equal to the minimum.')
        return
      }
      payload.salary_range = {
        currency: salaryCurrency,
        // Backend stores minor units (cents/pence) — multiply whole-unit
        // input by 100. UI rounds at display time.
        min_minor: Math.round(minNum * 100),
        max_minor: Math.round(maxNum * 100),
        period: salaryPeriod,
      }
    } else if (minSet || maxSet) {
      setError('Set both salary minimum and maximum, or leave both blank.')
      return
    } else if (isEdit) {
      // Both blank on an edit page → explicitly clear the disclosure.
      payload.salary_range = null
    }

    setSaving(true)
    try {
      if (isEdit) {
        const updated = await api.patch(`/api/b2b/jobs/${jobId}`, payload)
        navigate(`/b2b/jobs/${updated.id}`)
      } else {
        const created = await api.post('/api/b2b/jobs', payload)
        navigate(`/b2b/jobs/${created.id}`)
      }
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message
      setError(typeof detail === 'string' ? detail : 'Could not save job.')
      setSaving(false)
    }
  }

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-3xl mx-auto">
        <Link to="/b2b/jobs" className="inline-flex items-center gap-2 text-sm text-ink-3 hover:text-ink-1">
          <ArrowLeft className="w-4 h-4" />
          Back to jobs
        </Link>

        <h1 className="mt-4 text-2xl md:text-3xl font-bold text-ink-1">
          {isEdit ? 'Edit job' : 'New job'}
        </h1>
        <p className="text-sm text-ink-3 mt-1">
          Add structured sections — clear labels help AI screening in Phase 2.
        </p>

        {error && (
          <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {!eeoBannerDismissed && (
          <div className="mt-6 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 flex items-start gap-3">
            <Info className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" />
            <div className="flex-1 text-[13px] text-blue-900 leading-relaxed">
              <strong className="font-semibold">Equal opportunity reminder.</strong>{' '}
              Job postings must comply with applicable employment laws (EEOC/Title VII in the
              US, Equality Act 2010 in the UK, Directive 2000/78/EC in the EU). Avoid
              criteria related to age, gender, race, religion, disability, nationality, or
              other protected characteristics unless they are bona fide occupational
              qualifications.
            </div>
            <button
              type="button"
              onClick={() => setEeoBannerDismissed(true)}
              className="text-blue-700 hover:text-blue-900"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {loading ? (
          <p className="mt-8 text-sm text-ink-3">Loading…</p>
        ) : (
          <>
            <div className="mt-8 space-y-4">
              <Field label="Job title">
                <input
                  type="text"
                  placeholder="e.g., Senior Backend Engineer"
                  className="flex-1 text-[15px] outline-none bg-transparent"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={saving}
                />
              </Field>

              <CompanyPicker value={companyId} onChange={setCompanyId} disabled={saving} />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Department">
                  <input
                    type="text"
                    placeholder="e.g., Engineering"
                    className="flex-1 text-[15px] outline-none bg-transparent"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    disabled={saving}
                  />
                </Field>
                <Field label="Location">
                  <input
                    type="text"
                    placeholder="e.g., Remote / Berlin"
                    className="flex-1 text-[15px] outline-none bg-transparent"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    disabled={saving}
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Status">
                  <select
                    className="flex-1 text-[15px] outline-none bg-transparent"
                    value={statusValue}
                    onChange={(e) => setStatusValue(e.target.value)}
                    disabled={saving}
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                  </select>
                </Field>
                <Field
                  label="Default language"
                  hint="Default language for fit analysis on new candidates. Each screening has its own language (set when you create it) — change it there for per-interview overrides."
                >
                  <select
                    className="flex-1 text-[15px] outline-none bg-transparent"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    disabled={saving}
                  >
                    <option value="en">English</option>
                    <option value="ru">Russian</option>
                    <option value="uk">Ukrainian</option>
                    <option value="pl">Polish</option>
                    <option value="de">German</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                  </select>
                </Field>
              </div>

              <Field
                label="Role type"
                hint="Drives role-specific question patterns at screening time. AI auto-detects from the job description; you can override."
              >
                <select
                  className="flex-1 text-[15px] outline-none bg-transparent"
                  value={roleType}
                  onChange={(e) => {
                    setRoleType(e.target.value)
                    // Manual change → make it sticky on save (backend will
                    // flip role_type_auto_detected to false).
                    setRoleTypeAutoDetected(false)
                  }}
                  disabled={saving}
                >
                  {ROLE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </Field>

              {/* Auto-suggest banner — shown when classifier picked the value
                  AND has a confidence number to brag about. The recruiter
                  can dismiss by changing the dropdown above. */}
              {roleTypeAutoDetected && roleTypeConfidence != null && (
                <div className="flex items-start gap-3 rounded-2xl bg-accent/5 border border-accent/20 px-4 py-3 text-sm text-ink-2">
                  <Sparkles className="w-4 h-4 mt-0.5 text-accent flex-shrink-0" />
                  <div>
                    Auto-detected as <strong>{roleTypeLabel(roleType)}</strong>
                    {' '}
                    ({Math.round(roleTypeConfidence * 100)}% confidence).
                    {' '}
                    Change above if it's wrong — your override sticks.
                  </div>
                </div>
              )}

              {/* EU Pay Transparency Directive 2026 — disclose salary range. */}
              <div className="space-y-2">
                <div className="flex items-baseline justify-between">
                  <label className="text-xs font-bold text-ink-2 uppercase tracking-wide ml-1">
                    Salary range (EU pay transparency)
                  </label>
                  <span className="text-[11px] text-ink-3">
                    Required for EU postings from June 2026
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <select
                    className="text-[14px] outline-none bg-white border border-black/10 rounded-2xl px-3 py-3 focus:border-accent transition"
                    value={salaryCurrency}
                    onChange={(e) => setSalaryCurrency(e.target.value)}
                    disabled={saving}
                    aria-label="Currency"
                  >
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="USD">USD</option>
                    <option value="CHF">CHF</option>
                    <option value="PLN">PLN</option>
                  </select>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    step="1000"
                    placeholder="Min"
                    className="text-[14px] outline-none bg-white border border-black/10 rounded-2xl px-3 py-3 focus:border-accent transition"
                    value={salaryMin}
                    onChange={(e) => setSalaryMin(e.target.value)}
                    disabled={saving}
                    aria-label="Minimum salary"
                  />
                  <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    step="1000"
                    placeholder="Max"
                    className="text-[14px] outline-none bg-white border border-black/10 rounded-2xl px-3 py-3 focus:border-accent transition"
                    value={salaryMax}
                    onChange={(e) => setSalaryMax(e.target.value)}
                    disabled={saving}
                    aria-label="Maximum salary"
                  />
                  <select
                    className="text-[14px] outline-none bg-white border border-black/10 rounded-2xl px-3 py-3 focus:border-accent transition"
                    value={salaryPeriod}
                    onChange={(e) => setSalaryPeriod(e.target.value)}
                    disabled={saving}
                    aria-label="Period"
                  >
                    <option value="year">per year</option>
                    <option value="month">per month</option>
                    <option value="hour">per hour</option>
                  </select>
                </div>
                {/* Show the EU warning only when the job is being made
                    active without a salary disclosure. */}
                {statusValue === 'active' && (!salaryMin || !salaryMax) && (
                  <p className="text-[12px] text-amber-700 ml-1">
                    EU Pay Transparency Directive 2026 (effective June 2026)
                    requires a salary range on all EU job postings.
                  </p>
                )}
              </div>
            </div>

            <div className="mt-10">
              <h2 className="text-sm font-bold uppercase tracking-wider text-ink-3 mb-4">
                Role brief
              </h2>
              <BlockEditor
                blocks={blocks}
                onChange={setBlocks}
                addLabel="Add section"
                contentPlaceholder="Describe this section in plain text…"
              />
            </div>

            {isPublishing && (
              <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <ConsentCheckboxes
                  mode="publishJob"
                  values={consent}
                  onChange={updateConsent}
                />
              </div>
            )}

            <div className="mt-10 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate('/b2b/jobs')}
                disabled={saving}
                className="px-5 py-2.5 rounded-2xl text-sm font-semibold text-ink-2 hover:bg-canvas transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-5 py-2.5 rounded-2xl transition disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create job'}
              </button>
            </div>
          </>
        )}
      </div>
    </AppShell>
  )
}

function isServerUuid(id) {
  return typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-ink-2 uppercase tracking-wide ml-1">{label}</label>
      <div className="flex items-center gap-3 rounded-2xl border border-black/10 bg-white px-4 py-3.5 focus-within:border-accent transition-colors">
        {children}
      </div>
      {hint && <p className="text-[11px] text-ink-3 ml-1">{hint}</p>}
    </div>
  )
}
